import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client/react';
import i18n from 'i18next';
import SubjectHubConfigFieldEditDialog from './subject-hub-config-field-edit-dialog';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import DragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { useConditionallySynchronizedState } from '@hooks-clean/index';
import { RowDataSourceTableDto } from '@interfaces-clean/field-rdl-definition.interface';
import { normalizeFrontendKey } from '@utils-clean/column-name-helper';
import { useBoolean, useLocalState, useMap, useTranslation } from '@hooks/index';
import { Logger } from '@utils/logger';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    GET_PANELS,
    GET_STUDY_DASHBOARD_CONFIG_PANELS,
    GET_SUBJECT_HUB_CONFIG_FIELDS,
    SAVE_SUBJECT_HUB_CONFIG_FIELDS,
} from '@queries/central-monitoring';
import { SubjectHubConfigFieldListItem, SubjectHubConfigFormValues } from './types';

const COMPONENT_NAME = 'subjectHubConfig';

interface IProps {
    orgKey: string;
    studyRef: string;
    canEdit: boolean;
}

const mapDragDropListItemTitle = (label: string) => `${i18n.t('admin:subjectConfig.subjectHub.label')}: ${label}`;
const mapDragDropListItemSubtitle = (name: string) => `${i18n.t('admin:subjectConfig.subjectHub.field')}: ${name}`;

// These are fields that should not be brought over as they exist in Postgres but not Mongo
const configFieldsToExclude = new Set([
    'Subject.code',
    'Subject.id',
    'Subject.pgId',
    'Subject.parentPgId',
    'Subject.timelineBasisDateIgnore',
    'Subject.subjectNumber',
]);

const SubjectHubConfig = ({ orgKey, studyRef, canEdit }: IProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, setComponentHasChanges } = useUnsavedChangesContext();

    const { t: tGlobal } = useTranslation();
    const { t } = useTranslation('admin', {
        keyPrefix: 'subjectConfig.subjectHub',
    });
    const isEditMode = useBoolean(false);
    const { state } = useLocalState();

    const {
        data: configFields,
        loading: isFetching,
        error: loadError,
        refetch: fetchConfig,
    } = useQuery(GET_SUBJECT_HUB_CONFIG_FIELDS, {
        variables: { studyId: studyRef },
    });

    const allFields = useMemo<SubjectHubConfigFieldListItem[]>(() => {
        const configFieldsKeyToLabelMap = new Map(
            (configFields?.getSubjectHubConfigFields ?? [])
                // Only objects with defined keys and labels
                .filter((f) => f != null)
                .filter((f) => typeof f.key === 'string' && typeof f.label === 'string')
                .map((f) => [f.key, f.label]),
        );
        const subjectCM2Fields = (state?.cm2Fields ?? [])
            .filter((f) => f != null)
            // key and name can be null/undefined, despite what WebStorm claims...
            .filter((f) => normalizeFrontendKey(f.key) != null && f.name != null && f.fieldType != null)
            .filter((f) => f.dataType === RowDataSourceTableDto.SUBJECT);

        const illegalFields = new Set([
            ...configFieldsToExclude,
            ...subjectCM2Fields.filter((f) => f.key.endsWith('Ignore')).map((f) => f.key),
        ]);

        return subjectCM2Fields
            .filter((f) => !illegalFields.has(f.key))
            .map((f) => {
                const normalizedKey = normalizeFrontendKey(f.key)!;
                const label = configFieldsKeyToLabelMap.get(normalizedKey) ?? f.name!;
                return {
                    key: normalizedKey,
                    name: f.name!,
                    fieldType: f.fieldType!,
                    // Label of field (for study), if one has been configured
                    label,
                    title: mapDragDropListItemTitle(label),
                    subtitle: mapDragDropListItemSubtitle(f.name!),
                };
            });
    }, [configFields?.getSubjectHubConfigFields, state?.cm2Fields]);

    const [saveConfig, { error: saveError }] = useMutation(SAVE_SUBJECT_HUB_CONFIG_FIELDS, {
        refetchQueries: [
            {
                query: GET_PANELS,
                variables: { organisationId: orgKey, studyId: studyRef },
            },
            {
                query: GET_STUDY_DASHBOARD_CONFIG_PANELS,
                variables: { studyId: studyRef },
            },
        ],
    });

    const defaultValues = useMemo<SubjectHubConfigFormValues>(() => {
        const allFieldMap = new Map(allFields.map((f) => [f.key, f]));
        // The config may reference Study Plugin fields that have been removed from CM2.
        // These fields no longer appear in the CM2 Subject Fields list, so they cannot be
        // mapped normally. We therefore need special handling to ensure they can still be
        // identified and removed from the config.
        const removedFieldsMap = new Map(
            (configFields?.getSubjectHubConfigFields ?? [])
                .filter((f) => f?.key != null)
                .filter((f) => !allFieldMap.has(normalizeFrontendKey(f?.key!)))
                .map((f) => {
                    const normalizedKey = normalizeFrontendKey(f?.key!);
                    const label = f?.name!;

                    const mapped = {
                        key: normalizedKey,
                        name: label,
                        fieldType: f?.type!,
                        label,
                        title: mapDragDropListItemTitle(label),
                        subtitle: mapDragDropListItemSubtitle(label),
                        active: f?.key != null && allFieldMap.has(f.key),
                    };

                    return [normalizedKey, mapped];
                }),
        );

        // Map to fields
        const fields = (configFields?.getSubjectHubConfigFields ?? [])
            // Get fields that are defined and have a defined key
            .filter((x) => x != null)
            .filter((x) => x.key != null)
            // Sort by displayIndex.
            .toSorted((a, z) => {
                // If both are null, don't sort them.
                // If only 1 is null, the non-null one is first
                // Otherwise actually sort them in ascending order...
                if (a.displayIndex == null && z.displayIndex == null) {
                    return 0;
                }
                if (a.displayIndex == null) {
                    return 1;
                }
                if (z.displayIndex == null) {
                    return -1;
                }
                return a.displayIndex - z.displayIndex;
            })
            // Map to items of allFields list. No longer remove any that dont exist check the removedFieldsMap if we fail to match in allFields.
            .map((x) => allFieldMap.get(x.key!) || removedFieldsMap.get(x.key!))
            .filter((x) => x != null);

        return {
            fields,
        };
    }, [allFields, configFields?.getSubjectHubConfigFields]);

    const {
        control,
        reset,
        setValue,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
        watch,
    } = useForm<SubjectHubConfigFormValues>({
        mode: 'all',
        defaultValues,
    });

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [setComponentHasChanges, isDirty]);

    useEffect(() => {
        reset(defaultValues, { keepDirty: false });
    }, [reset, defaultValues]);

    const selectedFields = watch('fields');

    /*
     Maps field key to the latest label of the field (the last label entered into the form).

     In a scenario where the user:
     1. selects a field
     2. updates its label
     3. unselects the field
     4. re-selects the field
     The label entered on step 2 would be lost when step 3 occurs.

     So this map is used to track the label entered on step 2, so that it is not lost when step 3 occurs.
    */
    const fieldLatestLabelByKey = useMap<string, string>();

    // Fields for right column
    const availableFields = useMemo(() => {
        const selectedIdsSet = new Set(selectedFields.map((x) => x.key));

        // All fields which aren't already selected (in left column)
        return allFields
            .filter((x) => !selectedIdsSet.has(x.key))
            .map((x) => {
                // If a local update to the label was made during this edit, use that for the label, instead of the db value
                const latestEnteredLabel = fieldLatestLabelByKey.value.get(x.key) ?? x.label;

                return {
                    ...x,
                    label: latestEnteredLabel,
                    title: mapDragDropListItemTitle(latestEnteredLabel),
                    active: true,
                };
            });
    }, [selectedFields, allFields, fieldLatestLabelByKey.value]);

    // Selected field to edit (by index)
    const [selectedFieldToEditIndex, setSelectedFieldToEditIndex] = useState<number>();

    function exitEditMode() {
        isEditMode.setFalse();
        setComponentHasChanges(COMPONENT_NAME, false);
        setSelectedFieldToEditIndex(undefined);
        // Latest labels entered only apply while form is being edited
        fieldLatestLabelByKey.clear();
    }

    const onSubmit = handleSubmit(async (values) => {
        try {
            await saveConfig({
                variables: {
                    fields: values.fields.map((field, index) => ({
                        key: normalizeFrontendKey(field.key),
                        name: field.name,
                        type: field.fieldType,
                        label: field.label,
                        displayIndex: index,
                    })),
                    studyId: studyRef,
                },
            });
            exitEditMode();
            ShowSuccess(tGlobal('actionsSuccessfulToast'));
            await fetchConfig();
        } catch (error) {
            Logger.error(error, 'Study Hub Admin', 'onSave', 'Failed to save configured fields.');
            ShowError(tGlobal('errorMessage'));
        }
    });

    const onEditCardCancel = async () => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return;
            }
            reset(defaultValues, { keepDirty: false });
        }
        exitEditMode();
    };

    // Working copy of lists for left/right columns of DragDropListCore components
    // Since the DragDropListCore component keeps track of additional properties like `selected` and `filtered`,
    //   which aren't tracked by the `selectedFields` form value or `availableFields` list

    const [selectedFieldsForDragDropList, setSelectedFieldsForDragDropList] = useConditionallySynchronizedState<
        DragDropListItemProps[]
    >({
        value: selectedFields,
        // When not in edit mode, keep list synchronized with form value
        synchronize: !isEditMode.value,
    });

    const [availableFieldsForDragDropList, setAvailableFieldsForDragDropList] = useConditionallySynchronizedState<
        DragDropListItemProps[]
    >({
        value: availableFields,
        // When not in edit mode, keep list synchronized with true list (which doesn't track selected state)
        synchronize: !isEditMode.value,
    });

    const isLoading = isFetching;
    const isError = loadError != null || saveError != null;
    const canSubmit = isDirty && isValid && !isSubmitting;

    return (
        <EditCard
            title={t('title')}
            onEdit={isEditMode.setTrue}
            isEditMode={isEditMode.value}
            onCancel={onEditCardCancel}
            onSave={onSubmit}
            disabled={isLoading || !canEdit}
            saveDisabled={!canSubmit}
            data-testid="subject-hub-config"
        >
            {isLoading ? (
                <Loading />
            ) : isError ? (
                <ErrorMessage />
            ) : (
                <div style={{ height: 'calc(100vh - 224px - var(--page-banners-height)' }}>
                    <Controller
                        control={control}
                        name="fields"
                        render={({ field: { onChange } }) => (
                            <DragDropListsCore
                                list1Header={t('dragDropListTitles.selectedList')}
                                list2Header={t('dragDropListTitles.availableList')}
                                list1Items={selectedFieldsForDragDropList}
                                list2Items={availableFieldsForDragDropList}
                                isTwoLists
                                renderItemLeft={(item) => (
                                    <DragDropItem
                                        {...item}
                                        editButtonClick={(itemClicked) => {
                                            const index = selectedFields.findIndex((x) => x.key === itemClicked.key);

                                            if (index !== -1) {
                                                setSelectedFieldToEditIndex(index);
                                            }
                                        }}
                                        toDisplayColumn
                                    />
                                )}
                                renderItemRight={(item) => <DragDropItem {...item} toDisplayColumn />}
                                idField="key"
                                onChangeItemsLeft={(items) => {
                                    // Update working copy, which includes stuff like `selected` and `filtered`
                                    setSelectedFieldsForDragDropList(items);

                                    // Update true form value, which doesn't include stuff like `selected` and `filtered`
                                    // Because those properties would interfere with the form `isDirty` detection
                                    const mapped = items.map(({ selected, filtered, ...rest }) => rest);
                                    onChange(mapped);
                                }}
                                onChangeItemsRight={setAvailableFieldsForDragDropList}
                                disabled={!isEditMode.value}
                                className="k-mb-4"
                            />
                        )}
                    />
                    {/* If editing field (by index), show dialog for field being edited */}
                    {selectedFieldToEditIndex != null && selectedFields[selectedFieldToEditIndex] != null && (
                        <SubjectHubConfigFieldEditDialog
                            // If the index somehow changes while a dialog is open, reset the dialog for the field at new index
                            key={selectedFieldToEditIndex}
                            defaultValues={selectedFields[selectedFieldToEditIndex]}
                            // Close dialog without changes
                            onCancel={() => {
                                setSelectedFieldToEditIndex(undefined);
                            }}
                            // Update label and close dialog
                            onSubmit={({ label }) => {
                                const newFieldValue: SubjectHubConfigFieldListItem = {
                                    ...selectedFields[selectedFieldToEditIndex],
                                    label,
                                    // also update title (for UI)
                                    title: mapDragDropListItemTitle(label),
                                };

                                // Track latest label entered for field
                                fieldLatestLabelByKey.set(newFieldValue.key, newFieldValue.label);

                                // Update true form value
                                setValue(`fields.${selectedFieldToEditIndex}`, newFieldValue, { shouldDirty: true });
                                setSelectedFieldToEditIndex(undefined);

                                // Update working copy for DragDropListCore
                                // Get latest value of fields, since `selectedFields` will not include updated field yet
                                const fieldsLatest = watch('fields');
                                setSelectedFieldsForDragDropList([...fieldsLatest]);
                            }}
                        />
                    )}
                </div>
            )}
        </EditCard>
    );
};

export default SubjectHubConfig;
